import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatUnit } from '../../utils/formatUnit';
import { conversionNote } from '../../utils/chartConversion';

/**
 * Dezenter Hinweis unter dem Chart, wenn das Backend umgerechnet hat (Kursbasis,
 * ausgelassene Tage ohne Kurs) oder eine gewuenschte Umrechnung nicht moeglich
 * war (meta.reason; Werte und Beschriftung bleiben dann im Original).
 *
 * entries: Array<{ key, name, display (resolveSeriesDisplay), meta }>
 */
const ConversionNote = ({ entries = [], forecastSuppressed = false }) => {
  const { t } = useTranslation();
  const withName = entries.length > 1;
  const prefix = (entry) => (withName && entry.name ? `${entry.name}: ` : '');

  const items = [];
  const seen = new Set();
  const push = (key, text, extra = {}) => {
    if (!text || seen.has(text)) return;
    seen.add(text);
    items.push({ key, text, ...extra });
  };

  const notes = entries
    .map((entry) => ({ entry, note: conversionNote(entry.meta, entry.display) }))
    .filter((x) => x.note);

  const fxNotes = notes.filter((x) => x.note.kind !== 'unit');
  if (fxNotes.length) {
    const { note } = fxNotes[0];
    let text;
    if (note.kind === 'ecb') text = t('charts.conversion.note.ecb');
    else if (note.basis) text = t('charts.conversion.note.fxBasis', { basis: note.basis });
    else text = t('charts.conversion.note.fx');
    const seriesLines = fxNotes
      .filter((x) => x.note.fxSeries.length)
      .map((x) => `${withName && x.entry.name ? `${x.entry.name}: ` : ''}${x.note.fxSeries.join(', ')}`);
    const title = seriesLines.length
      ? `${t('charts.conversion.note.seriesTitle')}:\n${seriesLines.join('\n')}`
      : undefined;
    push('fx', text, { title });
  }

  notes
    .filter((x) => x.note.kind === 'unit')
    .forEach(({ entry }) => {
      push(`unit-${entry.key}`, `${prefix(entry)}${t('charts.conversion.note.unit', {
        from: formatUnit(entry.display.sourceUnit),
        to: formatUnit(entry.display.unit),
      })}`);
    });

  notes
    .filter((x) => x.note.skipped > 0)
    .forEach(({ entry, note }) => {
      push(`skipped-${entry.key}`, `${prefix(entry)}${t('charts.conversion.note.skipped', { count: note.skipped })}`);
    });

  entries
    .filter((entry) => entry.display?.failed)
    .forEach((entry) => {
      push(
        `failed-${entry.key}`,
        `${prefix(entry)}${entry.display.reason || t('charts.conversion.reasonFallback')}`,
        { warning: true }
      );
    });

  if (forecastSuppressed) {
    push('forecast', t('charts.conversion.forecastOriginalOnly'));
  }

  if (!items.length) return null;

  return (
    <div className="chart-conversion-note" data-testid="chart-conversion-note" aria-live="polite">
      {items.map((item) => (
        <span
          key={item.key}
          className={`chart-conversion-note-item${item.warning ? ' chart-conversion-note-warning' : ''}`}
          title={item.title}
        >
          {item.text}
        </span>
      ))}
    </div>
  );
};

export default ConversionNote;
